import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import Zone from "@insite/client-framework/Components/Zone";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { breakpointMediaQueries } from "@insite/mobius/utilities";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import FitImage from "@ucs/Components/Common/FitImage";
import UCSColors from "@ucs/Utils/UCSColors";
import * as React from "react";
import { css } from "styled-components";

const enum fields {
    image = "image",
    imageSide = "imageSide",
    bgColor = "bgColor",
    focalPoint = "focalPoint",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.image]: string;
        [fields.imageSide]: string;
        [fields.bgColor]: string;
        [fields.focalPoint]:
            | "topLeft"
            | "topCenter"
            | "topRight"
            | "centerLeft"
            | "center"
            | "centerRight"
            | "bottomLeft"
            | "bottomCenter"
            | "bottomRight";
    };
}

type Props = OwnProps;

export interface ProjectSectionalContentStyles {
    wrapper?: InjectableCss;
    content?: InjectableCss;
    container?: InjectableCss;
}

export const projectSectionalContentStyles: ProjectSectionalContentStyles = {
    container: {
        css: css`
            width: 100%;
            height: 100%;
            position: relative;
            overflow: hidden;
            ${({ theme }) => {
                return breakpointMediaQueries(theme, [
                    css`
                        height: 190px;
                        order: 1;
                    `,
                    css`
                        height: 240px;
                        order: 1;
                    `,
                    css`
                        height: 320px;
                        order: 1;
                    `,
                    css``,
                ]);
            }}
        `,
    },
    wrapper: {
        css: css`
            width: 100%;
            height: fit-content;
            display: grid;
            grid-template-columns: repeat(2, 1fr);
            ${({ theme }) => {
                return breakpointMediaQueries(theme, [
                    css`
                        grid-template-columns: 100%;
                    `,
                    css`
                        grid-template-columns: 100%;
                    `,
                    css`
                        grid-template-columns: 100%;
                    `,
                ]);
            }}
        `,
    },
    content: {
        css: css`
            width: 100%;
            min-height: 700px;
            padding: 96px 130px;
            display: flex;
            flex-direction: column;
            justify-content: center;
            align-items: center;
            gap: 24px;
            & > * {
                min-width: 460px;
                max-width: 660px;
                ${({ theme }) => {
                    return breakpointMediaQueries(theme, [
                        css`
                            max-width: 100%;
                            min-width: 300px;
                        `,
                        css`
                            max-width: 100%;
                        `,
                        css``,
                        css`
                            min-width: 400px;
                        `,
                    ]);
                }}
            }

            ${({ theme }) => {
                return breakpointMediaQueries(theme, [
                    css`
                        min-height: 350px;
                        padding: 48px 20px;
                        order: 2;
                    `,
                    css`
                        min-height: 480px;
                        padding: 54px 40px;
                        order: 2;
                    `,
                    css`
                        min-height: 490px;
                        padding: 66px 60px;
                        order: 2;
                    `,
                    css`
                        min-height: 450px;
                        padding: 66px 30px;
                    `,
                    css`
                        min-height: 680px;
                    `,
                ]);
            }}
            @media only screen and (min-width: 1140px) and (max-width: 1280px) {
                padding: 66px 35px;
            }
            @media only screen and (min-width: 1140px) and (max-width: 1280px) {
                padding: 80px 55px;
            }
            @media only screen and (min-width: 1280px) and (max-width: 1444px) {
                padding: 90px 90px;
            }
        `,
    },
};

const styles = projectSectionalContentStyles;

const ProjectSectionalContent: React.FC<Props> = ({ id, fields }) => {
    const { image, bgColor, imageSide, focalPoint } = fields;
    const contentStyles = css`
        ${styles?.content?.css}
        background-color: ${bgColor};
    `;

    return (
        <StyledWrapper {...styles.wrapper}>
            {imageSide === "left" && (
                 <FitImage image={image} focalPoint={focalPoint} containerStyles={styles.container}/>
            )}

            <StyledWrapper css={contentStyles}>
                <Zone contentId={id} zoneName="Content1" />
                <Zone contentId={id} zoneName="Content2" />
            </StyledWrapper>

            {imageSide === "right" && (
                 <FitImage image={image} focalPoint={focalPoint} containerStyles={styles.container}/>
            )}
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: ProjectSectionalContent,
    definition: {
        group: "Common",
        fieldDefinitions: [
            {
                name: fields.image,
                editorTemplate: "ImagePickerField",
                defaultValue: "",
                fieldType: "Translatable",
                isRequired: true,
            },
            {
                name: fields.imageSide,
                displayName: "Image Side",
                editorTemplate: "RadioButtonsField",
                options: [
                    {
                        displayName: "Right",
                        value: "right",
                    },
                    {
                        displayName: "Left",
                        value: "left",
                    },
                ],
                defaultValue: "left",
                fieldType: "Translatable",
            },
            {
                name: fields.bgColor,
                displayName: "Background Color",
                editorTemplate: "ColorPickerField",
                defaultValue: `${UCSColors.gray09}`,
                fieldType: "General",
            },
            {
                fieldType: "General",
                name: fields.focalPoint,
                displayName: "Focal Point",
                editorTemplate: "DropDownField",
                options: [
                    { displayName: "Top Left", value: "topLeft" },
                    { displayName: "Top Center", value: "topCenter" },
                    { displayName: "Top Right", value: "topRight" },
                    { displayName: "Center Left", value: "centerLeft" },
                    { displayName: "Center", value: "center" },
                    { displayName: "Center Right", value: "centerRight" },
                    { displayName: "Bottom Left", value: "bottomLeft" },
                    { displayName: "Bottom Center", value: "bottomCenter" },
                    { displayName: "Bottom Right", value: "bottomRight" },
                ],
                defaultValue: "center",
                hideEmptyOption: true,
            },
        ],
    },
};

export default widgetModule;
